/**
 * Contract review API calls.
 */
import axios from 'axios';
import { getContractApiBase, getApiHeaders } from './api';
import { getAccessToken } from '../../../shared/auth/okta';
import type {
  CategoryInfo,
  ContractSession,
  ContractUploadResponse,
  PaginatedSessionsResponse,
  PaginatedClausesResponse,
  AssessClausesResponse,
  AddClauseRequest,
  UpdateClauseRequest,
  ApproveExampleRequest,
  ClauseExampleResponse,
  ClauseResponse,
  SummaryResponse,
  UpdateSummaryRequest,
  PasteClausesRequest,
  SplitClausesResponse,
  TranslateClauseResponse,
} from '../types/contracts';

export interface GetSessionsParams {
  page?: number;
  page_size?: number;
  client_name?: string;
}

/**
 * List contract review sessions (newest first). Paginated with optional client name search.
 * GET /api/contracts/sessions
 */
export async function getSessions(params: GetSessionsParams = {}): Promise<PaginatedSessionsResponse> {
  const accessToken = getAccessToken();
  const search = new URLSearchParams();
  if (params.page != null) search.set('page', String(params.page));
  if (params.page_size != null) search.set('page_size', String(params.page_size));
  if (params.client_name != null && params.client_name.trim() !== '')
    search.set('client_name', params.client_name.trim());
  const query = search.toString();
  const url = `${getContractApiBase()}/sessions${query ? `?${query}` : ''}`;
  const { data } = await axios.get<PaginatedSessionsResponse>(url, {
    headers: getApiHeaders(accessToken),
  });
  return data;
}

export async function getSession(sessionUuid: string): Promise<ContractSession> {
  const accessToken = getAccessToken();
  const { data } = await axios.get<ContractSession>(
    `${getContractApiBase()}/${sessionUuid}`,
    { headers: getApiHeaders(accessToken) }
  );
  return data;
}

export interface GetClausesParams {
  page?: number;
  page_size?: number;
  category?: string;
  reviewed?: boolean;
  is_manual?: boolean;
  billable_flag?: boolean;
}

/**
 * Get clauses for a session.
 * GET /api/contracts/{session_uuid}/clauses
 * Returns paginated clauses with optional filtering.
 */
export async function getClauses(
  sessionUuid: string,
  params: GetClausesParams = {}
): Promise<PaginatedClausesResponse> {
  const accessToken = getAccessToken();
  const search = new URLSearchParams();
  if (params.page != null) search.set('page', String(params.page));
  if (params.page_size != null) search.set('page_size', String(params.page_size));
  if (params.category) search.set('category', params.category);
  if (params.reviewed != null) search.set('reviewed', String(params.reviewed));
  if (params.is_manual != null) search.set('is_manual', String(params.is_manual));
  if (params.billable_flag != null) search.set('billable_flag', String(params.billable_flag));
  const query = search.toString();
  const url = `${getContractApiBase()}/${sessionUuid}/clauses${query ? `?${query}` : ''}`;
  const { data } = await axios.get<PaginatedClausesResponse>(url, {
    headers: getApiHeaders(accessToken),
  });
  return data;
}

/**
 * Trigger AI assessment of extracted clauses for a session.
 * POST /api/contracts/{session_uuid}/assess
 * Returns updated clauses with position, tw_response, internal_actions, suggested_modification.
 */
export async function postAssess(sessionUuid: string): Promise<AssessClausesResponse> {
  const accessToken = getAccessToken();
  const { data } = await axios.post<AssessClausesResponse>(
    `${getContractApiBase()}/${sessionUuid}/assess`,
    {},
    { headers: getApiHeaders(accessToken) }
  );
  return data;
}

/**
 * Add a clause manually to a session.
 * POST /api/contracts/{session_uuid}/clauses
 */
export async function postAddClause(
  sessionUuid: string,
  body: AddClauseRequest
): Promise<ClauseResponse> {
  const accessToken = getAccessToken();
  const { data } = await axios.post<ClauseResponse>(
    `${getContractApiBase()}/${sessionUuid}/clauses`,
    body,
    { headers: getApiHeaders(accessToken) }
  );
  return data;
}

/**
 * Delete a clause.
 * DELETE /api/contracts/clauses/{clause_uuid}
 */
export async function deleteClause(clauseUuid: string): Promise<void> {
  const accessToken = getAccessToken();
  await axios.delete(`${getContractApiBase()}/clauses/${clauseUuid}`, {
    headers: getApiHeaders(accessToken),
  });
}

/**
 * Approve a clause assessment and save it to the Examples Store (flywheel).
 * POST /api/contracts/clauses/{clause_uuid}/approve
 */
export async function postApproveExample(
  clauseUuid: string,
  body: ApproveExampleRequest
): Promise<ClauseExampleResponse> {
  const accessToken = getAccessToken();
  const { data } = await axios.post<ClauseExampleResponse>(
    `${getContractApiBase()}/clauses/${clauseUuid}/approve`,
    body,
    { headers: getApiHeaders(accessToken) }
  );
  return data;
}

/**
 * Get all available KB category tags for the category dropdown.
 * GET /api/contracts/categories
 */
export async function getCategories(): Promise<CategoryInfo[]> {
  const accessToken = getAccessToken();
  const { data } = await axios.get<{ categories: CategoryInfo[] }>(
    `${getContractApiBase()}/categories`,
    { headers: getApiHeaders(accessToken) }
  );
  return data.categories;
}

/**
 * Update a clause with human review (position, client comment, internal actions).
 * PATCH /api/contracts/clauses/{clause_uuid}
 */
export async function patchClause(
  clauseUuid: string,
  body: UpdateClauseRequest
): Promise<ClauseResponse> {
  const accessToken = getAccessToken();
  const { data } = await axios.patch<ClauseResponse>(
    `${getContractApiBase()}/clauses/${clauseUuid}`,
    body,
    { headers: getApiHeaders(accessToken) }
  );
  return data;
}

/**
 * Translate a clause's original text to English (source language auto-detected).
 * POST /api/contracts/clauses/{clause_uuid}/translate
 */
export async function translateClause(
  clauseUuid: string
): Promise<TranslateClauseResponse> {
  const accessToken = getAccessToken();
  const { data } = await axios.post<TranslateClauseResponse>(
    `${getContractApiBase()}/clauses/${clauseUuid}/translate`,
    {},
    { headers: getApiHeaders(accessToken) }
  );
  return data;
}

/**
 * Generate AI summary of clause assessments for a session.
 * POST /api/contracts/{session_uuid}/summary
 */
export async function postGenerateSummary(
  sessionUuid: string
): Promise<SummaryResponse> {
  const accessToken = getAccessToken();
  const { data } = await axios.post<SummaryResponse>(
    `${getContractApiBase()}/${sessionUuid}/summary`,
    {},
    { headers: getApiHeaders(accessToken) }
  );
  return data;
}

/**
 * Update final summary (human-edited) for a session.
 * PATCH /api/contracts/{session_uuid}/summary
 */
export async function patchSummary(
  sessionUuid: string,
  body: UpdateSummaryRequest
): Promise<SummaryResponse> {
  const accessToken = getAccessToken();
  const { data } = await axios.patch<SummaryResponse>(
    `${getContractApiBase()}/${sessionUuid}/summary`,
    body,
    { headers: getApiHeaders(accessToken) }
  );
  return data;
}

/**
 * Create a contract session from pasted clauses (no file upload).
 * POST /api/contracts/paste
 */
export async function postPasteClauses(
  body: PasteClausesRequest
): Promise<ContractUploadResponse> {
  const accessToken = getAccessToken();
  const { data } = await axios.post<ContractUploadResponse>(
    `${getContractApiBase()}/paste`,
    body,
    { headers: getApiHeaders(accessToken) }
  );
  return data;
}

/**
 * Use AI to split a block of pasted text into individual contract clauses.
 * POST /api/contracts/split-clauses
 */
export async function postSplitClauses(
  rawText: string
): Promise<SplitClausesResponse> {
  const accessToken = getAccessToken();
  const { data } = await axios.post<SplitClausesResponse>(
    `${getContractApiBase()}/split-clauses`,
    { raw_text: rawText },
    { headers: getApiHeaders(accessToken) }
  );
  return data;
}
